import moment from "moment";
import { ReactNode, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";
import { AdminPageHeader } from "../../../components/admin/adminPageHeader";
import { Button } from "../../../components/buttons/button";
import { PopoverMenu } from "../../../components/dropdown/popover";
import { CustomDateComponent } from "../../../components/inputs/customDateComponent";
import { RevenueTable } from "../../../components/revenue/revenueTable";
import { TabNav } from "../../../components/tabNav";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useAdminGetAttentionQueueQuery,
  useAdminGetEventInsightsQuery,
  useAdminGetPlatformHealthQuery,
  useAdminGetRevenueInsightsQuery,
  useAdminGetRevenueTableQuery,
  useAdminGetSalesInsightsQuery,
  useAdminGetUserInsightsQuery,
} from "../../../redux/features/admin/payments/payments.api";
import { InsightGraphPoint } from "../../../redux/features/admin/insights.types";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../redux/hooks";
import { currencyShorts } from "../../../utils/constants";
import {
  formatDate,
  formatToCurrency,
  getDisplayRange,
  normalizeDate,
} from "../../../utils/helpers";
import { DateFilter, FrequencyFilter, QueryParams } from "../../../utils/types";

type DateRangeState = {
  filter?: DateFilter;
  startDate?: string;
  endDate?: string;
};

type Summary = Record<string, number | string | undefined>;

type AttentionQueueItem = {
  id?: string;
  _id?: string;
  type?: string;
  title?: string;
  subtitle?: string;
  severity?: "low" | "medium" | "high" | string;
  createdAt?: string;
  actionUrl?: string;
  [key: string]: unknown;
};

type InsightPayload = {
  summary?: Summary;
  deltas?: Summary;
  meta?: {
    dateBasis?: unknown;
    [key: string]: unknown;
  };
  revenueGraph?: InsightGraphPoint[];
  eventsGraph?: InsightGraphPoint[];
  userGrowthGraph?: InsightGraphPoint[];
  salesGraph?: InsightGraphPoint[];
  currencyBreakdown?: Summary[];
  paymentProviderBreakdown?: Summary[];
  topEventsByRevenue?: Summary[];
  topAgents?: Summary[];
  items?: AttentionQueueItem[];
};

const currencyOptions: { label: string; value: PayoutCurrency }[] = [
  { label: "NGN", value: "NGN" },
  { label: "CAD", value: "Canadian Dollars" },
];

const frequencyTabs: { label: string; value: FrequencyFilter }[] = [
  { label: "Days", value: "daily" },
  { label: "Weeks", value: "weekly" },
  { label: "Months", value: "monthly" },
];

export const OverviewPage = () => {
  const { adminProfile } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRangeState>({
    filter: "all",
    startDate: undefined,
    endDate: undefined,
  });
  const [frequency, setFrequency] = useState<FrequencyFilter>("daily");
  const [currency, setCurrency] = useState<PayoutCurrency>("NGN");
  const [revenueQuery, setRevenueQuery] = useState<QueryParams>({
    page: 1,
    limit: 20,
  });

  const dateParams = useMemo(() => getDateParams(dateRange), [dateRange]);
  const insightQuery = useMemo(
    () => ({
      ...dateParams,
      filter: frequency,
      currency,
    }),
    [currency, dateParams, frequency],
  );

  const {
    isFetching: fetchingPlatformHealth,
    data: platformHealth,
    isError: platformHealthError,
  } = useAdminGetPlatformHealthQuery(insightQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isFetching: fetchingRevenueInsights,
    data: revenueInsights,
    isError: revenueInsightsError,
  } = useAdminGetRevenueInsightsQuery(insightQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isFetching: fetchingEventInsights,
    data: eventInsights,
    isError: eventInsightsError,
  } = useAdminGetEventInsightsQuery(insightQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isFetching: fetchingUserInsights,
    data: userInsights,
    isError: userInsightsError,
  } = useAdminGetUserInsightsQuery(insightQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isFetching: fetchingSalesInsights,
    data: salesInsights,
    isError: salesInsightsError,
  } = useAdminGetSalesInsightsQuery(insightQuery, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isFetching: fetchingAttentionQueue,
    data: attentionQueue,
    isError: attentionQueueError,
  } = useAdminGetAttentionQueueQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { isFetching: fetchingRevenueData, data: revenueData } =
    useAdminGetRevenueTableQuery(
      { ...revenueQuery, currency },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const platformPayload = getInsightPayload(platformHealth);
  const revenuePayload = getInsightPayload(revenueInsights);
  const eventPayload = getInsightPayload(eventInsights);
  const userPayload = getInsightPayload(userInsights);
  const salesPayload = getInsightPayload(salesInsights);
  const attentionPayload = getInsightPayload(attentionQueue);

  const platformSummary = platformPayload?.summary ?? {};
  const platformDeltas = platformPayload?.deltas ?? {};
  const revenueSummary = revenuePayload?.summary ?? {};
  const eventSummary = eventPayload?.summary ?? {};
  const userSummary = userPayload?.summary ?? {};
  const salesSummary = salesPayload?.summary ?? {};
  const attentionSummary = attentionPayload?.summary ?? {};
  const hasInsightError =
    platformHealthError ||
    revenueInsightsError ||
    eventInsightsError ||
    userInsightsError ||
    salesInsightsError ||
    attentionQueueError;

  return (
    <div className="w-full">
      <AdminPageHeader
        title={`Hello ${adminProfile?.firstName ?? "there"}`}
        subtitle={getDisplayRange(
          dateRange.filter,
          dateRange.startDate,
          dateRange.endDate,
        )}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <CustomDateComponent
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
              }}
            />
            <CurrencyDropdown currency={currency} setCurrency={setCurrency} />
          </div>
        }
      />

      {hasInsightError && (
        <div className="mt-6 rounded-2xl border border-lighter-red bg-light-red p-3 text-sm font-bold text-danger">
          Some dashboard insights could not be loaded. Check the Network tab for
          the failing admin insight endpoint.
        </div>
      )}

      <section className="mt-8 grid grid-cols-1 gap-3 xl:grid-cols-[1fr_360px]">
        <BgWrapper className="flex flex-col gap-4">
          <SectionHeader title="Platform Health" />
          <div className="grid grid-cols-1 gap-[10px] sm:grid-cols-2 lg:grid-cols-4">
            <InsightMetricCard
              label="Users"
              value={getMetric(platformSummary, ["totalUsers", "users"])}
              delta={getMetric(platformDeltas, ["totalUsers", "users"])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Organizers"
              value={getMetric(platformSummary, [
                "totalOrganizers",
                "totalOrganisers",
                "organizers",
                "organisers",
              ])}
              delta={getMetric(platformDeltas, [
                "totalOrganizers",
                "totalOrganisers",
              ])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Events"
              value={getMetric(platformSummary, ["totalEvents", "events"])}
              delta={getMetric(platformDeltas, ["totalEvents", "events"])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Paid Tickets"
              value={getMetric(platformSummary, [
                "totalPaidTicketsSold",
                "totalPaidTickets",
                "paidTicketsSold",
              ])}
              delta={getMetric(platformDeltas, [
                "totalPaidTicketsSold",
                "paidTicketsSold",
              ])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Successful Orders"
              value={getMetric(platformSummary, [
                "successfulOrders",
                "successfulPayments",
              ])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Failed Orders"
              value={getMetric(platformSummary, [
                "failedOrders",
                "failedPayments",
              ])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Unverified Payouts"
              value={getMetric(platformSummary, [
                "unverifiedPayoutAccounts",
                "pendingPayoutAccounts",
              ])}
              loading={fetchingPlatformHealth}
            />
            <InsightMetricCard
              label="Suspended Users"
              value={getMetric(platformSummary, ["suspendedUsers"])}
              loading={fetchingPlatformHealth}
            />
          </div>
        </BgWrapper>

        <AttentionQueuePanel
          items={attentionPayload?.items ?? []}
          summary={attentionSummary}
          loading={fetchingAttentionQueue}
          onItemClick={(url) => {
            if (url) navigate(url);
          }}
        />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-[1.2fr_0.8fr]">
        <BgWrapper className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <SectionHeader title="Revenue Insights" />
            <TabNav
              navs={frequencyTabs.map((tab) => ({
                title: tab.label,
                to: tab.value,
                onClick: () => setFrequency(tab.value),
              }))}
              variant="switcher"
              theme="plain"
              activeTab={frequency}
              className="h-fit"
            />
          </div>
          <div className="grid grid-cols-1 gap-[10px] md:grid-cols-3">
            <InsightMetricCard
              label="Gross Ticket Sales"
              value={getMetric(revenueSummary, [
                "grossTicketSales",
                "amountProcessed",
                "totalPaidTicketAmount",
              ])}
              loading={fetchingRevenueInsights}
              isCurrency
              currency={currency}
            />
            <InsightMetricCard
              label="Tikket Revenue"
              value={getMetric(revenueSummary, [
                "tikketRevenue",
                "totalRevenue",
                "revenue",
              ])}
              loading={fetchingRevenueInsights}
              isCurrency
              currency={currency}
            />
            <InsightMetricCard
              label="Profit"
              value={getMetric(revenueSummary, ["profit", "totalTicketProfit"])}
              loading={fetchingRevenueInsights}
              isCurrency
              currency={currency}
            />
            <InsightMetricCard
              label="Amount Paid Out"
              value={getMetric(revenueSummary, [
                "amountPaidOut",
                "totalPaidOut",
              ])}
              loading={fetchingRevenueInsights}
              isCurrency
              currency={currency}
            />
            <InsightMetricCard
              label="Pending Payouts"
              value={getMetric(revenueSummary, ["pendingPayouts"])}
              loading={fetchingRevenueInsights}
              isCurrency
              currency={currency}
            />
            <InsightMetricCard
              label="Paid Tickets Sold"
              value={getMetric(revenueSummary, [
                "paidTicketsSold",
                "totalTicketsSold",
              ])}
              loading={fetchingRevenueInsights}
            />
          </div>
          <InsightAreaChart
            data={revenuePayload?.revenueGraph ?? []}
            loading={fetchingRevenueInsights}
            frequency={frequency}
            currency={currency}
            valueKeys={[
              "grossTicketSales",
              "tikketRevenue",
              "revenue",
              "profit",
            ]}
            label="Revenue"
            isCurrency
          />
        </BgWrapper>

        <BgWrapper className="flex flex-col gap-4">
          <SectionHeader title="Payment Mix" />
          <BreakdownList
            title="Currency"
            rows={revenuePayload?.currencyBreakdown ?? []}
            primaryKeys={["currency"]}
            valueKeys={[
              "grossTicketSales",
              "tikketRevenue",
              "revenue",
              "paidTicketsSold",
            ]}
            currency={currency}
          />
          <BreakdownList
            title="Providers"
            rows={revenuePayload?.paymentProviderBreakdown ?? []}
            primaryKeys={["provider", "paymentProvider", "name"]}
            valueKeys={[
              "successfulPayments",
              "grossTicketSales",
              "failedPayments",
            ]}
            currency={currency}
          />
        </BgWrapper>
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-3">
        <InsightPanel
          title="Event Insights"
          loading={fetchingEventInsights}
          currency={currency}
          cards={[
            {
              label: "Published Events",
              value: getMetric(eventSummary, [
                "publishedEvents",
                "eventsPublished",
              ]),
            },
            {
              label: "Paid Events",
              value: getMetric(eventSummary, ["paidEvents"]),
            },
            {
              label: "Flagged Events",
              value: getMetric(eventSummary, ["flaggedEvents"]),
            },
            {
              label: "Avg Revenue/Event",
              value: getMetric(eventSummary, ["averageRevenuePerPaidEvent"]),
              isCurrency: true,
            },
          ]}
          chart={
            <InsightAreaChart
              data={eventPayload?.eventsGraph ?? []}
              loading={fetchingEventInsights}
              frequency={frequency}
              currency={currency}
              valueKeys={["ticketsSold", "grossTicketSales", "eventsCreated"]}
              label="Events"
            />
          }
        />

        <InsightPanel
          title="User Growth"
          loading={fetchingUserInsights}
          currency={currency}
          cards={[
            {
              label: "New Users",
              value: getMetric(userSummary, ["newUsers"]),
            },
            {
              label: "New Organizers",
              value: getMetric(userSummary, ["newOrganizers", "newOrganisers"]),
            },
            {
              label: "Active Organizers",
              value: getMetric(userSummary, [
                "activeOrganizers",
                "activeOrganisers",
              ]),
            },
            {
              label: "Repeat Organizers",
              value: getMetric(userSummary, [
                "repeatOrganizers",
                "repeatOrganisers",
              ]),
            },
          ]}
          chart={
            <InsightAreaChart
              data={userPayload?.userGrowthGraph ?? []}
              loading={fetchingUserInsights}
              frequency={frequency}
              currency={currency}
              valueKeys={["newUsers", "newOrganizers", "activeOrganizers"]}
              label="Users"
            />
          }
        />

        <InsightPanel
          title="Sales Insights"
          loading={fetchingSalesInsights}
          currency={currency}
          cards={[
            {
              label: "Sales Agents",
              value: getMetric(salesSummary, ["totalSalesAgents"]),
            },
            {
              label: "Assigned Organizers",
              value: getMetric(salesSummary, [
                "assignedOrganizers",
                "assignedOrganisers",
              ]),
            },
            {
              label: "Attributed Sales",
              value: getMetric(salesSummary, [
                "attributedGrossTicketSales",
                "grossTicketSales",
              ]),
              isCurrency: true,
            },
            {
              label: "Commission Earned",
              value: getMetric(salesSummary, [
                "totalCommissionEarned",
                "commissionEarned",
              ]),
              isCurrency: true,
            },
          ]}
          chart={
            <InsightAreaChart
              data={salesPayload?.salesGraph ?? []}
              loading={fetchingSalesInsights}
              frequency={frequency}
              currency={currency}
              valueKeys={[
                "attributedGrossTicketSales",
                "commissionEarned",
                "attributedTicketsSold",
              ]}
              label="Sales"
            />
          }
        />
      </section>

      <section className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <InsightList
          title="Top Events By Revenue"
          rows={eventPayload?.topEventsByRevenue ?? []}
          loading={fetchingEventInsights}
          primaryKeys={["name", "eventName", "title"]}
          secondaryKeys={["organizerName", "organiserName", "currency"]}
          valueKeys={["grossTicketSales", "revenue", "ticketsSold"]}
          currency={currency}
          emptyText="Top revenue events will show here."
        />
        <InsightList
          title="Top Sales Agents"
          rows={salesPayload?.topAgents ?? []}
          loading={fetchingSalesInsights}
          primaryKeys={["name", "agentName", "fullName"]}
          secondaryKeys={["assignedOrganizers", "activeOrganizers"]}
          valueKeys={[
            "attributedGrossTicketSales",
            "commissionEarned",
            "attributedTicketsSold",
          ]}
          currency={currency}
          emptyText="Top agents will show here."
        />
      </section>

      <section className="mt-3">
        <BgWrapper
          loading={fetchingRevenueData}
          loaderHeight={48}
          className="flex w-full items-center justify-between !rounded-b-none border-b border-b-grey-50 py-4"
        >
          <div>
            <MiniHeader header="Revenue MoM" />
          </div>
          <Link
            to="/overview/revenue"
            className="text-base font-bold text-blue-500"
          >
            View Revenue
          </Link>
        </BgWrapper>
        <BgWrapper className="mt-[2px] w-full !rounded-t-none">
          <RevenueTable
            fetchingRevenueData={fetchingRevenueData}
            revenueData={revenueData}
            query={revenueQuery}
            setQuery={setRevenueQuery}
            clickable
          />
        </BgWrapper>
      </section>
    </div>
  );
};

const getDateParams = (range: DateRangeState) => {
  if (range.startDate && range.endDate) {
    return {
      startDate: range.startDate,
      endDate: range.endDate,
    };
  }

  switch (range.filter) {
    case "today":
      return {
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      };
    case "yesterday":
      return {
        startDate: moment().subtract(1, "day").format("YYYY-MM-DD"),
        endDate: moment().subtract(1, "day").format("YYYY-MM-DD"),
      };
    case "this-week":
      return {
        startDate: moment().startOf("week").format("YYYY-MM-DD"),
        endDate: moment().endOf("week").format("YYYY-MM-DD"),
      };
    case "this-month":
      return {
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().endOf("month").format("YYYY-MM-DD"),
      };
    default:
      return {};
  }
};

const getInsightPayload = (response?: unknown): InsightPayload | undefined => {
  if (!response) return undefined;

  const raw = response as InsightPayload & { data?: InsightPayload };
  const nested = raw.data;

  if (nested && isInsightPayload(nested)) {
    return nested;
  }

  return raw;
};

const isInsightPayload = (value: InsightPayload) => {
  return Boolean(
    value.summary ||
      value.meta ||
      value.revenueGraph ||
      value.eventsGraph ||
      value.userGrowthGraph ||
      value.salesGraph ||
      value.items,
  );
};

const CurrencyDropdown = ({
  currency,
  setCurrency,
}: {
  currency: PayoutCurrency;
  setCurrency: (currency: PayoutCurrency) => void;
}) => {
  return (
    <PopoverMenu
      className="min-w-max"
      position="bottom end"
      menuClassName="!min-w-fit !z-10 rounded-[8px] bg-white border border-grey-100 !shadow-sm ![--anchor-gap:0px]"
      customMenuItems={({ close }) => {
        return (
          <div className="flex w-[160px] flex-col overflow-hidden rounded-xl bg-white">
            {currencyOptions.map((option, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setCurrency(option.value);
                  close();
                }}
                className="w-full border-b border-b-grey-100 px-4 py-3 text-left text-sm last:border-b-0"
              >
                <span className="font-bold text-grey-950">{option.label}</span>
              </button>
            ))}
          </div>
        );
      }}
    >
      {({ open }) => (
        <Button
          type="button"
          text={currencyShorts[currency]}
          icon="caret-down"
          iconFirst={false}
          iconDimension={20}
          variation="styled-grey"
          className={`capitalize ${open ? "bg-grey-50" : ""}`}
        />
      )}
    </PopoverMenu>
  );
};

const SectionHeader = ({ title }: { title: ReactNode }) => {
  return (
    <div>
      <MiniHeader header={title} className="text-primary" />
    </div>
  );
};

const InsightMetricCard = ({
  label,
  value,
  delta,
  loading = false,
  isCurrency = false,
  currency = "NGN",
}: {
  label: string;
  value?: number | string;
  delta?: number | string;
  loading?: boolean;
  isCurrency?: boolean;
  currency?: PayoutCurrency;
}) => {
  return (
    <div className="rounded-2xl border border-grey-200 bg-white p-3">
      <p className="text-xs font-bold uppercase tracking-[0.08em] text-grey-500">
        {label}
      </p>
      <p className="mt-2 text-xl font-extrabold text-grey-950">
        {loading ? "-" : formatValue(value, { isCurrency, currency })}
      </p>
      {delta !== undefined && (
        <p className="mt-1 text-xs font-bold text-grey-600">
          {formatDelta(delta)} vs previous period
        </p>
      )}
    </div>
  );
};

const InsightPanel = ({
  title,
  cards,
  chart,
  currency,
}: {
  title: string;
  loading?: boolean;
  currency: PayoutCurrency;
  cards: {
    label: string;
    value?: number | string;
    isCurrency?: boolean;
  }[];
  chart: ReactNode;
}) => {
  return (
    <BgWrapper className="flex flex-col gap-4">
      <SectionHeader title={title} />
      <div className="grid grid-cols-2 gap-2">
        {cards.map((card) => (
          <InsightMetricCard
            key={card.label}
            label={card.label}
            value={card.value}
            isCurrency={card.isCurrency}
            currency={currency}
          />
        ))}
      </div>
      {chart}
    </BgWrapper>
  );
};

const InsightAreaChart = ({
  data,
  loading,
  frequency,
  currency,
  valueKeys,
  label,
  isCurrency = false,
}: {
  data: InsightGraphPoint[];
  loading: boolean;
  frequency: FrequencyFilter;
  currency: PayoutCurrency;
  valueKeys: string[];
  label: string;
  isCurrency?: boolean;
}) => {
  const valueKey = valueKeys.find((key) =>
    data.some((entry) => entry[key] !== undefined),
  );
  const chartData = !loading
    ? data.map((entry) => ({
        label: formatGraphLabel(String(entry.label), frequency),
        rawLabel: entry.label,
        value: Number(valueKey ? (entry[valueKey] ?? 0) : 0),
      }))
    : [];

  return (
    <div className="h-[220px] w-full rounded-2xl border border-grey-100 p-3">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id={`insight-${label}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#98C8F7" stopOpacity={0.5} />
              <stop offset="95%" stopColor="#98C8F7" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="3 0"
            vertical={false}
            stroke="#E7ECF2"
          />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            className="!font-fustat !text-sm !text-grey-600"
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload?.length) return null;
              return (
                <div className="rounded-[4px] border border-grey-100 bg-white px-3 py-[6px] text-sm shadow-md">
                  <p className="font-bold text-grey-800">
                    {formatValue(payload[0]?.value as number, {
                      isCurrency,
                      currency,
                    })}
                  </p>
                  <p className="text-grey-600">
                    {String(payload[0]?.payload?.rawLabel ?? "")}
                  </p>
                </div>
              );
            }}
          />
          <Area
            type="linear"
            dataKey="value"
            stroke="#1976D2"
            strokeWidth={2}
            fillOpacity={1}
            fill={`url(#insight-${label})`}
            activeDot={{
              r: 5,
              stroke: "#1976D2",
              fill: "white",
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const AttentionQueuePanel = ({
  items,
  summary,
  loading,
  onItemClick,
}: {
  items: AttentionQueueItem[];
  summary: Summary;
  loading: boolean;
  onItemClick: (url?: string) => void;
}) => {
  return (
    <BgWrapper className="flex flex-col gap-4">
      <SectionHeader title="Attention Queue" />
      <div className="grid grid-cols-2 gap-2">
        <InsightMetricCard
          label="Flagged"
          value={getMetric(summary, ["flaggedEvents"])}
          loading={loading}
        />
        <InsightMetricCard
          label="Failed Payouts"
          value={getMetric(summary, ["failedPayouts"])}
          loading={loading}
        />
        <InsightMetricCard
          label="Failed Payments"
          value={getMetric(summary, ["failedPayments"])}
          loading={loading}
        />
        <InsightMetricCard
          label="Holidays"
          value={getMetric(summary, ["holidaysRequiringAttention"])}
          loading={loading}
        />
      </div>
      <div className="flex max-h-[360px] flex-col gap-2 overflow-y-auto">
        {loading ? (
          <p className="rounded-2xl bg-grey-50 p-3 text-sm font-bold text-grey-600">
            Loading attention queue
          </p>
        ) : items.length ? (
          items.slice(0, 8).map((item, idx) => (
            <button
              key={item.id ?? item._id ?? `${item.type}-${idx}`}
              type="button"
              className="rounded-2xl border border-grey-100 p-3 text-left hover:bg-grey-50"
              onClick={() => onItemClick(item.actionUrl)}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-extrabold text-grey-950">
                    {item.title ?? item.type ?? "Attention item"}
                  </p>
                  {item.subtitle && (
                    <p className="mt-1 text-sm text-grey-600">
                      {item.subtitle}
                    </p>
                  )}
                </div>
                <span
                  className={`rounded-full px-2 py-1 text-xs font-bold capitalize ${
                    item.severity === "high"
                      ? "bg-light-red text-danger"
                      : item.severity === "medium"
                        ? "bg-lighter-yellow text-yellow"
                        : "bg-grey-50 text-grey-600"
                  }`}
                >
                  {item.severity ?? "low"}
                </span>
              </div>
              {item.createdAt && (
                <p className="mt-2 text-xs font-bold text-grey-500">
                  {formatDate(item.createdAt, "MMM DD, YYYY")}
                </p>
              )}
            </button>
          ))
        ) : (
          <p className="rounded-2xl bg-grey-50 p-3 text-sm font-bold text-grey-600">
            No attention items right now.
          </p>
        )}
      </div>
    </BgWrapper>
  );
};

const InsightList = ({
  title,
  rows,
  loading,
  primaryKeys,
  secondaryKeys,
  valueKeys,
  currency,
  emptyText,
}: {
  title: string;
  rows: Summary[];
  loading: boolean;
  primaryKeys: string[];
  secondaryKeys: string[];
  valueKeys: string[];
  currency: PayoutCurrency;
  emptyText: string;
}) => {
  return (
    <BgWrapper className="flex flex-col gap-3">
      <SectionHeader title={title} />
      <div className="flex flex-col gap-2">
        {loading ? (
          <p className="rounded-2xl bg-grey-50 p-3 text-sm font-bold text-grey-600">
            Loading {title.toLowerCase()}
          </p>
        ) : rows.length ? (
          rows.slice(0, 6).map((row, idx) => (
            <div
              key={`${title}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-2xl border border-grey-100 p-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-grey-950">
                  {String(getMetric(row, primaryKeys) ?? "Untitled")}
                </p>
                <p className="mt-1 truncate text-sm text-grey-600">
                  {secondaryKeys
                    .map((key) => row[key])
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <p className="soft-shrink text-right text-sm font-extrabold text-grey-950">
                {formatInsightListValue(row, valueKeys, currency)}
              </p>
            </div>
          ))
        ) : (
          <p className="rounded-2xl bg-grey-50 p-3 text-sm font-bold text-grey-600">
            {emptyText}
          </p>
        )}
      </div>
    </BgWrapper>
  );
};

const BreakdownList = ({
  title,
  rows,
  primaryKeys,
  valueKeys,
  currency,
}: {
  title: string;
  rows: Summary[];
  primaryKeys: string[];
  valueKeys: string[];
  currency: PayoutCurrency;
}) => {
  return (
    <div>
      <p className="mb-2 text-sm font-extrabold text-grey-950">{title}</p>
      <div className="flex flex-col gap-2">
        {rows.length ? (
          rows.slice(0, 4).map((row, idx) => (
            <div
              key={`${title}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-xl bg-grey-50 p-3"
            >
              <p className="text-sm font-bold text-grey-800">
                {String(getMetric(row, primaryKeys) ?? "Unknown")}
              </p>
              <p className="text-right text-sm font-extrabold text-grey-950">
                {formatInsightListValue(row, valueKeys, currency)}
              </p>
            </div>
          ))
        ) : (
          <p className="rounded-xl bg-grey-50 p-3 text-sm font-bold text-grey-600">
            No breakdown available.
          </p>
        )}
      </div>
    </div>
  );
};

const getMetric = (summary: Summary, keys: string[]) => {
  for (const key of keys) {
    const value = summary[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const formatValue = (
  value?: number | string,
  options?: { isCurrency?: boolean; currency?: PayoutCurrency },
) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (options?.isCurrency) {
    return formatToCurrency(numericValue, {
      currency: currencyShorts[options.currency ?? "NGN"],
    });
  }
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString()
    : String(value ?? 0);
};

const formatDelta = (delta?: number | string) => {
  const numericValue = Number(delta ?? 0);
  if (!Number.isFinite(numericValue)) return String(delta);
  const prefix = numericValue > 0 ? "+" : "";
  return `${prefix}${numericValue.toLocaleString()}%`;
};

const formatGraphLabel = (label: string, frequency: FrequencyFilter) => {
  if (frequency === "daily") return formatDate(label, "MMM DD");
  if (frequency === "monthly") {
    return formatDate(`${label}-01`, "MMM", false, true);
  }
  return normalizeDate(label);
};

const formatInsightListValue = (
  row: Summary,
  valueKeys: string[],
  currency: PayoutCurrency,
) => {
  const key = valueKeys.find((k) => row[k] !== undefined);
  if (!key) return "-";
  const value = row[key];
  const shouldFormatCurrency =
    key.toLowerCase().includes("sales") ||
    key.toLowerCase().includes("revenue") ||
    key.toLowerCase().includes("commission") ||
    key.toLowerCase().includes("amount");

  return formatValue(value, {
    isCurrency: shouldFormatCurrency,
    currency,
  });
};
